import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { AlertTriangle, CheckCircle, User, Loader2, IdCard } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * PendingIdentificationAlertModal - Displays a blocking alert when a participant has a highlighted
 * "Pending Identification Verification" case note.
 * 
 * Behavior matches ExpirationAlertModal:
 * - Assigned User sees "Acknowledge" button (permanent suppression)
 * - Non-assigned User sees "Bypass" button (temporary, reappears after 24 hours)
 */
const PendingIdentificationAlertModal = ({ 
  participantId, 
  participantName,
  onClose, 
  onAcknowledged,
  user 
}) => {
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchAlert = async () => {
      if (!participantId) return;
      
      try {
        setLoading(true);
        const response = await axios.get(`${API}/participants/${participantId}/pending-id-verification-alert`);
        
        if (response.data.has_alert) {
          setAlert(response.data.alert);
        } else {
          // No alert needed
          onClose?.();
        }
      } catch (error) {
        console.error('Failed to fetch pending ID verification alert:', error);
        onClose?.();
      } finally {
        setLoading(false);
      }
    };

    fetchAlert();
  }, [participantId, onClose]);

  const handleAcknowledge = async () => {
    if (!alert) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/participants/${participantId}/pending-id-verification-alert/${alert.id}/acknowledge`);
      toast.success('Alert acknowledged successfully');
      onAcknowledged?.();
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge alert');
    } finally {
      setProcessing(false);
    }
  };

  const handleBypass = async () => {
    if (!alert) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/participants/${participantId}/pending-id-verification-alert/${alert.id}/bypass`);
      toast.info('Alert bypassed for this session. It will reappear when the profile is opened again.');
      onClose?.();
    } catch (error) {
      if (error.response?.data?.detail?.includes('must acknowledge')) {
        toast.warning('As the Assigned User, you must acknowledge this alert.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to bypass alert');
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!alert) {
    return null;
  }

  const isAssignedUser = alert.is_assigned_user;

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-lg" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="pending-id-verification-alert-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <IdCard className="h-6 w-6 text-amber-600" />
            </div>
            <DialogTitle className="text-xl text-amber-700 dark:text-amber-400">
              Pending Identification Verification
            </DialogTitle>
          </div>
          <DialogDescription className="text-base">
            Alert for <span className="font-medium text-foreground">{participantName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Alert Message */}
          <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-amber-800 dark:text-amber-200 leading-relaxed">
                  {alert.message}
                </p>
              </div>
            </div>
          </div>

          {/* Case Assignment Info */}
          <div className="p-3 rounded-lg bg-muted/50 border text-sm">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Assigned User:</span>
              <span className="font-medium">
                {alert.assigned_user_name || 'Unassigned'}
              </span>
              {isAssignedUser && (
                <span className="ml-auto px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                  You
                </span>
              )}
            </div>
          </div>

          {/* Action Explanation */}
          <div className="text-sm text-muted-foreground">
            {isAssignedUser ? (
              <p>
                <CheckCircle className="inline h-4 w-4 text-green-600 mr-1" />
                As the <span className="font-medium">Assigned User</span>, click <span className="font-medium">Acknowledge</span> to confirm 
                you are aware of this requirement. The alert will be permanently dismissed.
              </p>
            ) : (
              <p>
                <AlertTriangle className="inline h-4 w-4 text-orange-500 mr-1" />
                You are not the Assigned User on this case. Click <span className="font-medium">Bypass</span> to dismiss 
                this alert temporarily. It will reappear when you open this profile again.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {isAssignedUser ? (
            <Button 
              onClick={handleAcknowledge} 
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="acknowledge-id-alert-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Acknowledge
                </>
              )}
            </Button>
          ) : (
            <Button 
              onClick={handleBypass} 
              disabled={processing}
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
              data-testid="bypass-id-alert-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Bypass for Now'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PendingIdentificationAlertModal;
